import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

const PWAInstallContext = createContext(null);

const detectIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPad on iPadOS 13+ reports as Mac; detect via touch support.
  const iPadOS = /Macintosh/.test(ua) && 'ontouchend' in document;
  return iOS || iPadOS;
};

const detectStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export const PWAInstallProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(detectStandalone());
  const [isIOS] = useState(detectIOS());

  useEffect(() => {
    const handleBeforeInstall = (event) => {
      // Prevent Chrome from showing its own mini-infobar so we can show ours.
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    const mq = window.matchMedia('(display-mode: standalone)');
    const handleModeChange = (ev) => setIsInstalled(ev.matches);
    if (mq.addEventListener) mq.addEventListener('change', handleModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      if (mq.removeEventListener) mq.removeEventListener('change', handleModeChange);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice;
  };

  return (
    <PWAInstallContext.Provider
      value={{
        canInstall: Boolean(deferredPrompt),
        isInstalled,
        isIOS,
        promptInstall,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
};

export const usePWAInstall = () => {
  const ctx = useContext(PWAInstallContext);
  if (!ctx) {
    throw new Error('usePWAInstall must be used inside PWAInstallProvider');
  }
  return ctx;
};